/**
 * Site header: scrolled shadow, mobile menu panel and dropdown submenus.
 *
 * Submenus follow the disclosure pattern — the parent label stays a link, the
 * chevron <button aria-expanded aria-controls> toggles the submenu.
 *  - Desktop (≥ 1200px, hover devices): open on hover with a short close delay,
 *    and on click/keyboard via the chevron. Level 3+ flips left near the edge.
 *  - Mobile: chevrons expand accordions; the burger opens the full-screen panel.
 */

// Keep in sync with breakpoint-up(xl) in _header.scss ($breakpoints xl).
const DESKTOP = window.matchMedia('(min-width: 1200px)');
const CAN_HOVER = window.matchMedia('(hover: hover)');
const CLOSE_DELAY = 150;

export function initHeader(root = document) {
  const header = root.querySelector('[data-site-header]');
  if (!header) return;

  const nav = header.querySelector('[data-site-nav]');
  const burger = header.querySelector('[data-nav-toggle]');
  const items = [...header.querySelectorAll('[data-nav-item]')];

  // ---------------------------------------------------------------- scroll
  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // ------------------------------------------------------------- submenus
  const toggleOf = (li) => li.querySelector(':scope > [data-nav-sub-toggle]');
  const timers = new WeakMap();

  const setOpen = (li, open) => {
    clearTimeout(timers.get(li));
    li.classList.toggle('is-open', open);
    toggleOf(li)?.setAttribute('aria-expanded', String(open));
    if (open) {
      // Close siblings (one open branch per level).
      li.parentElement.querySelectorAll(':scope > .is-open').forEach((sib) => {
        if (sib !== li) setOpen(sib, false);
      });
      if (DESKTOP.matches) flipIfNeeded(li);
    } else {
      li.querySelectorAll('.is-open').forEach((child) => setOpen(child, false));
    }
  };

  const flipIfNeeded = (li) => {
    const sub = li.querySelector(':scope > .site-nav__list');
    if (!sub || sub.classList.contains('site-nav__list--depth-2')) return;
    li.classList.remove('is-flipped');
    if (sub.getBoundingClientRect().right > window.innerWidth - 8) li.classList.add('is-flipped');
  };

  items.forEach((li) => {
    toggleOf(li)?.addEventListener('click', () => setOpen(li, !li.classList.contains('is-open')));

    li.addEventListener('mouseenter', () => {
      if (DESKTOP.matches && CAN_HOVER.matches) setOpen(li, true);
    });
    li.addEventListener('mouseleave', () => {
      if (!DESKTOP.matches || !CAN_HOVER.matches) return;
      timers.set(li, setTimeout(() => setOpen(li, false), CLOSE_DELAY));
    });

    // Desktop: close when focus leaves the item entirely.
    li.addEventListener('focusout', (e) => {
      if (DESKTOP.matches && !li.contains(e.relatedTarget)) setOpen(li, false);
    });
  });

  // ------------------------------------------------------------ mobile panel
  const isMenuOpen = () => header.classList.contains('is-open');

  const setMenu = (open) => {
    header.classList.toggle('is-open', open);
    burger?.setAttribute('aria-expanded', String(open));
    burger?.setAttribute('aria-label', open ? 'Stäng meny' : 'Meny');
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) {
      nav?.querySelector('a, button')?.focus({ preventScroll: true });
    }
  };

  burger?.addEventListener('click', () => setMenu(!isMenuOpen()));

  // Following a link (incl. in-page anchors) closes the panel.
  nav?.addEventListener('click', (e) => {
    if (e.target.closest('a') && isMenuOpen()) setMenu(false);
  });

  DESKTOP.addEventListener('change', (e) => {
    if (e.matches && isMenuOpen()) setMenu(false);
    items.forEach((li) => setOpen(li, false));
  });

  // ---------------------------------------------------------------- escape
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    // Mobile: Escape closes the whole panel.
    if (!DESKTOP.matches && isMenuOpen()) {
      setMenu(false);
      burger?.focus();
      return;
    }
    const openItem = e.target.closest?.('[data-nav-item].is-open') || items.find((li) => li.classList.contains('is-open'));
    if (openItem) {
      setOpen(openItem, false);
      toggleOf(openItem)?.focus();
      return;
    }
    if (isMenuOpen()) {
      setMenu(false);
      burger?.focus();
    }
  });

  // Click outside closes open dropdowns (desktop).
  document.addEventListener('click', (e) => {
    if (!DESKTOP.matches || header.contains(e.target)) return;
    items.forEach((li) => li.classList.contains('is-open') && setOpen(li, false));
  });
}
